// src/client/views/converter/ConverterIssues.tsx
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Container, Pagination, Input, CheckPicker } from 'rsuite'
import { Table } from '@/client/components/Controls'
import { PanelError } from '@/client/components/ErrorProvider'
import Loading from '@/client/components/Loading'
import useQuery from '@/client/utils/useQuery'
import ConvertIssuesModel from '@/client/models/convertIssues'

const { Column, HeaderCell, Cell } = Table
const issuesModel = new ConvertIssuesModel()

type Props = { tab: any }

const TYPE_OPTIONS = [
    { label: 'warning', value: 'warning' },
    { label: 'error', value: 'error' },
]

export default function ConverterIssues({ tab }: Props) {
    const { t } = useTranslation()
    const { projectId, outTableId, label, fileId } = tab?.store || {} // <-- include fileId

    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(50)

    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [types, setTypes] = useState<Array<'warning' | 'error'>>([]) // empty = all

    // debounce search so we don't spam the API
    useEffect(() => {
        const id = setTimeout(() => setDebouncedSearch(search.trim()), 350)
        return () => clearTimeout(id)
    }, [search])

    // reset to first page when targets/filters change
    useEffect(() => {
        setPage(1)
    }, [projectId, outTableId, fileId, debouncedSearch, types, limit])

    // useQuery refetch trigger
    const refreshCounter = useMemo(
        () =>
            Number(
                `${projectId}-${outTableId}-${fileId ?? ''}-${page}-${limit}-${debouncedSearch}-${types.join(',')}`
                    .length
            ),
        [projectId, outTableId, fileId, page, limit, debouncedSearch, types]
    )

    const { data, loading, error } = useQuery(issuesModel.list, {
        args: {
            projectId,
            outTableId,
            page,
            limit,
            q: debouncedSearch || undefined,
            types,
            fileId,
        },
        allowEmptyVariables: false,
        refreshCounter,
    })

    const items = data?.items || []
    const total = data?.total || 0

    if (error) return <PanelError message={error.message} />
    if (loading && !data) return <Loading />

    return (
        <Container className="content-view">
            <div className="converterlist-top-navigation-actions">
                <div className="breadcrumb">
                    {t('Converter Issues')}: {label || outTableId}
                    {fileId ? ` — file ${fileId}` : ''}
                </div>

                {/* Filters */}
                <div className="actions" style={{ display: 'flex', gap: 8 }}>
                    <Input placeholder={t('Search…')} value={search} onChange={(val) => setSearch(val)} />
                    <CheckPicker
                        data={TYPE_OPTIONS}
                        placeholder={t('Type: All')}
                        value={types}
                        onChange={(vals) => setTypes(vals as Array<'warning' | 'error'>)}
                        style={{ width: 220 }}
                        cleanable
                    />
                </div>
            </div>

            <Table
                key={`${outTableId}-${fileId ?? 'all'}-${page}-${limit}-${debouncedSearch}-${types.join(',')}`}
                data={items}
                height={600}
                wordWrap
                rowKey="_id"
                autoHeight={false}
                loading={loading}
            >
                <Column width={50} resizable>
                    <HeaderCell>{t('#')}</HeaderCell>
                    <Cell>
                        {(row: any) => (typeof row.inTableRowIndex === 'number' ? row.inTableRowIndex + 1 : '')}
                    </Cell>
                </Column>

                <Column width={110} resizable>
                    <HeaderCell>{t('Type')}</HeaderCell>
                    <Cell dataKey="type" />
                </Column>

                <Column width={120} resizable>
                    <HeaderCell>{t('In Column')}</HeaderCell>
                    <Cell dataKey="inColumnLabel" />
                </Column>

                <Column width={120} resizable>
                    <HeaderCell>{t('Out Column')}</HeaderCell>
                    <Cell dataKey="outColumnLabel" />
                </Column>

                <Column width={160} resizable>
                    <HeaderCell>{t('Original')}</HeaderCell>
                    <Cell dataKey="original" />
                </Column>

                <Column width={200} resizable>
                    <HeaderCell>{t('Result')}</HeaderCell>
                    <Cell dataKey="result" />
                </Column>

                <Column width={340} resizable>
                    <HeaderCell>{t('Message')}</HeaderCell>
                    {/* from the language files where the issue carries a key */}
                    <Cell dataKey="message">
                        {(row: any) => (row.messageKey ? t(row.messageKey, row.params) || row.message : row.message)}
                    </Cell>
                </Column>

                <Column width={160} resizable>
                    <HeaderCell>{t('Mapping Type')}</HeaderCell>
                    <Cell dataKey="mappingType" />
                </Column>

                <Column width={200} resizable>
                    <HeaderCell>{t('Function Name')}</HeaderCell>
                    <Cell dataKey="functionName" />
                </Column>
            </Table>

            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>
                    {t('Total')}: {total}
                </span>
                <Pagination
                    prev
                    next
                    first
                    last
                    ellipsis
                    boundaryLinks
                    layout={['total', '-', 'limit', '|', 'pager']}
                    total={total}
                    limit={limit}
                    limitOptions={[20, 50, 100, 200]}
                    activePage={page}
                    disabled={loading}
                    onChangePage={(p) => setPage(p)}
                    onChangeLimit={(l) => setLimit(l)}
                />
            </div>
        </Container>
    )
}
